"use client";

import React, { useEffect, Ref, useImperativeHandle, useRef } from "react";

// components
import LocationSpacesContainerFilters from "./components/LocationSpacesContainerFilters";
import LocationSpacesContainerBody from "./components/LocationSpacesContainerBody";

// helpers
import useQueryParams from "@/hooks/useQueryParams";
import useCurrency from "@/hooks/useCurrency";
import locations from "@/api/query/locations";

// interfaces
import { ISingleLocationResponse } from "@/types/api/SingleLocationResponse";

// assets
import styles from "@/assets/styles/pages/location-single-page/location-single-page-components.module.scss";
import separateSpaceBySpaceAssetType from "@/globals/separateSpaceBySpaceAssetType";


export const INITIAL_AVAILABILITY_MODAL_SETTINGS = {
  open: false,
  slug: null,
};

export interface ILocationSpacesContainer {
  locationCurrency: string;
  locationData: ISingleLocationResponse;
  isPreview?: boolean;
  isRestricted?: boolean;
  onRestrictedButtonClick?: (e: React.MouseEvent<HTMLElement>) => void;
  handleSendAnalytics?: () => void;
}

const LocationSpacesContainer = React.forwardRef((props: ILocationSpacesContainer, ref) => {
  const {
    locationCurrency,
    locationData,
    isPreview,
    isRestricted,
    handleSendAnalytics,
    onRestrictedButtonClick,
  } = props;

  const blockRef = useRef<HTMLDivElement | null>(null);

  const [{
    preview,
    purpose__in,
    type__in,
    rate_daily__range,
    width__gte,
    length__gte,
    size__in,
    start_date,
    end_date
  }] = useQueryParams();
  const [currency] = useCurrency();
  const currencyToUse = currency || locationCurrency;

  const { data: spacesResp, isLoading, refetch, remove } = locations.useLocationSpaces({
    slug: locationData?.slug,
    params: {
      limit: 100,
      status__in: preview ? "0__1__2__3" : null,
      ordering: "position",
      purpose__in,
      type__in,
      rate_daily__range,
      width__gte,
      length__gte,
      size__in,
      ranges__in: start_date && end_date ? `${start_date}__${end_date}` : null
    },
    config: { enabled: !isPreview }
  });

  const spacesList = spacesResp?.results || [];
  const spacesCount = spacesResp?.count || 0;

  const {
    activationSpaces,
    mediaOpportunitySpaces
  } = separateSpaceBySpaceAssetType(spacesList) || {};

  // effects
  useEffect(() => remove, []);

  useImperativeHandle(ref, () => ({
    refBlockForScroll: blockRef.current,
    refetch
  }));

  return (
    <section
      ref={blockRef as Ref<HTMLElement>}
      className={styles["locl-location-page-spaces-container"]}
      data-testid="locl-location-page-spaces-container"
    >
      <div className={styles["locl-location-page-spaces-container__head"]}>
        <h2
          className={styles["locl-location-page-spaces-container__title"]}
          data-testid="locl-location-page-spaces-container-title"
        >
          Spaces at this location ({spacesCount || 0})
        </h2>
      </div>
      <LocationSpacesContainerFilters
        isRestricted={isRestricted}
        onRestrictedButtonClick={onRestrictedButtonClick}
      />
      <LocationSpacesContainerBody
        title={`Activation spaces (${activationSpaces.length})`}
        currencyToUse={currencyToUse}
        spacesList={activationSpaces}
        isLoading={isLoading}
        isRestricted={isRestricted}
        onRestrictedButtonClick={onRestrictedButtonClick}
        refetch={refetch}
        handleSendAnalytics={handleSendAnalytics}
      />
      <LocationSpacesContainerBody
        title={`Media opportunities (${mediaOpportunitySpaces.length})`}
        currencyToUse={currencyToUse}
        spacesList={mediaOpportunitySpaces}
        isLoading={isLoading}
        isRestricted={isRestricted}
        onRestrictedButtonClick={onRestrictedButtonClick}
        refetch={refetch}
        handleSendAnalytics={handleSendAnalytics}
      />
    </section>
  );
});

LocationSpacesContainer.displayName = "LocationSpacesContainer";

export default LocationSpacesContainer;
